import { SystemFieldType } from '@constants/surveys';

export const CONTACT_FIELDS = ['email', 'phone', 'name', 'sex', 'dateOfBirth'] as const;

export type ContactField = (typeof CONTACT_FIELDS)[number];

export const CONTACT_FIELDS_DEFAULTS = {
    emailEnabled: true,
    emailRequired: true,
    phoneEnabled: false,
    phoneRequired: false,
    nameEnabled: false,
    nameRequired: false,
    sexEnabled: false,
    sexRequired: false,
    dateOfBirthEnabled: false,
    dateOfBirthRequired: false,
};

type ContactFieldsValues = Record<string, unknown>;

type SystemField = { type: number; enable: boolean; required: boolean };

type StoredSystemFields = Record<string, Partial<SystemField> | undefined> | null | undefined;

const IDENTIFYING_FIELDS: readonly ContactField[] = ['email', 'phone'];

const SYSTEM_FIELDS: Record<ContactField, { key: string; type: number }> = {
    email: { key: 'Email', type: SystemFieldType.EMAIL },
    phone: { key: 'Phone', type: SystemFieldType.PHONE },
    name: { key: 'Name', type: SystemFieldType.NAME },
    sex: { key: 'Sex', type: SystemFieldType.SEX },
    dateOfBirth: { key: 'DateOfBirth', type: SystemFieldType.DATE_OF_BIRTH },
};

export const enabledName = (field: ContactField) => `${field}Enabled`;
export const requiredName = (field: ContactField) => `${field}Required`;

export const contactFieldNames = (fields: readonly ContactField[] = CONTACT_FIELDS) =>
    fields.flatMap(field => [enabledName(field), requiredName(field)]);

export const pickContactFields = (values: ContactFieldsValues): Record<string, boolean> =>
    Object.fromEntries(contactFieldNames().map(name => [name, !!values[name]]));

export const omitContactFields = (values: ContactFieldsValues) => {
    const names = contactFieldNames();
    return Object.fromEntries(Object.entries(values).filter(([name]) => !names.includes(name)));
};

export const isFieldRequired = (values: ContactFieldsValues, field: ContactField) =>
    !!values[enabledName(field)] && !!values[requiredName(field)];

export const identifyingFieldsOf = (fields: readonly ContactField[]) =>
    fields.filter(field => IDENTIFYING_FIELDS.includes(field));

export const hasRequiredIdentifyingField = (values: ContactFieldsValues, fields: readonly ContactField[]) =>
    identifyingFieldsOf(fields).some(field => isFieldRequired(values, field));

export const toSystemFields = (values: ContactFieldsValues): Record<string, SystemField> =>
    Object.fromEntries(
        CONTACT_FIELDS.map(field => [
            SYSTEM_FIELDS[field].key,
            {
                type: SYSTEM_FIELDS[field].type,
                enable: !!values[enabledName(field)],
                required: !!values[requiredName(field)],
            },
        ]),
    );

export const fromSystemFields = (systemFields: StoredSystemFields): Record<string, boolean> =>
    Object.fromEntries(
        CONTACT_FIELDS.flatMap(field => {
            const systemField = systemFields?.[SYSTEM_FIELDS[field].key];
            return [
                [enabledName(field), !!systemField?.enable],
                [requiredName(field), !!systemField?.required],
            ];
        }),
    );
